import { CheckCheck } from 'lucide-react';
import ReplyCard from './ReplyCard';
import StarRating from './StarRating/StarRating';
import useResponsive from '../utils/useResponsive';

export default function ReviewCard(props) {
    const isMobile = useResponsive('down', 'sm');

    const attributionText = (text) => {
        if (String(text)?.length < 2) {
            return isMobile ? 'Collected online' : 'This review was collected online';
        }
        if (text?.includes('This review complies ')) {
            return isMobile
                ? 'Complies with ' + text?.replace('This review complies with ', '')
                : text;
        }
        if (text?.includes('This review was collected via ')) {
            return isMobile
                ? 'Collected via ' + text?.replace('This review was collected via ', '')
                : text;
        }
        return text;
    };

    const useAbsolute = props?.keepMinimal === true || props?.fixedHeight > 1;
    const absoluteBlock = useAbsolute
        ? 'absolute left-0 right-0 px-[25px]'
        : '';

    const truncatedText =
        props?.maxBodyLength > 1
            ? (props?.review?.text || '').substring(0, props.maxBodyLength) +
              ((props?.review?.text || '').length > props.maxBodyLength ? '...' : '')
            : props?.review?.text || '';
    const renderedText = truncatedText.replace(/(\r\n|\r|\n)/g, '<br />');

    return (
        <div
            className="rounded-xl border bg-card text-card-foreground shadow-sm relative"
            style={{ height: props?.fixedHeight > 1 ? props.fixedHeight : 'auto' }}
        >
            <div className="p-6">
                {/* Review title */}
                <div className={`grid grid-cols-12 ${absoluteBlock} ${useAbsolute ? 'top-[10px]' : ''}`}>
                    <div className="col-span-12 mb-0">
                        <h5 className="line-clamp-1 overflow-hidden text-lg font-semibold">
                            {props.review.title || 'Untitled review'}
                        </h5>
                    </div>
                </div>

                {/* Score and source logo */}
                <div className={`grid grid-cols-12 mb-6 ${absoluteBlock} ${useAbsolute ? 'top-[40px]' : ''}`}>
                    <div className="col-span-9 sm:col-span-6">
                        <StarRating value={props.review.rating.score} />
                    </div>
                    <div className="col-span-3 sm:col-span-6">
                        <p className="flex justify-end pt-[3px] text-sm text-slate-600">
                            <img
                                className="h-[25px] w-[25px]"
                                src={`/sources/platform-${props?.review?.platform?.toLowerCase()}.png`}
                                alt="Source logo"
                            />
                        </p>
                    </div>
                </div>

                {/* Company name (group widgets only) */}
                {props?.displayCompanyName === true && (
                    <p
                        className={`text-sm text-slate-600 ${useAbsolute ? 'absolute left-0 right-0 px-[25px] top-[67px]' : ''}`}
                    >
                        <small>
                            {props?.keepMinimal === true ? 'Review for' : 'This is a review for'}
                            &nbsp;
                            {props?.review?.clientName}
                        </small>
                    </p>
                )}

                {/* Review text */}
                <div
                    className={`grid grid-cols-12 ${absoluteBlock} ${
                        useAbsolute
                            ? props?.displayCompanyName === true
                                ? 'top-[92px]'
                                : 'top-[80px]'
                            : ''
                    }`}
                >
                    <div className="col-span-12">
                        {props.review?.text?.length > 0 ? (
                            <p
                                className={`mb-6 text-sm text-slate-600 ${
                                    props?.isReviewFeed !== true ? 'line-clamp-2 overflow-hidden' : ''
                                }`}
                                dangerouslySetInnerHTML={{ __html: renderedText }}
                            />
                        ) : (
                            <p className="mb-6 text-sm text-slate-600">
                                This person left a star rating but no comment.
                            </p>
                        )}
                    </div>
                </div>

                {/* Author and date */}
                <div
                    className={`grid grid-cols-12 ${useAbsolute ? 'absolute bottom-5 left-0 right-0 px-[25px]' : ''}`}
                >
                    <div className="col-span-12">
                        {props?.keepMinimal !== true ? (
                            <p className="pt-2.5 text-sm">
                                - {props.review.author?.length > 1 ? props.review.author : 'Anonymous'}
                            </p>
                        ) : (
                            <div className="grid grid-cols-12 gap-4 pt-2.5">
                                <div className="col-span-7">
                                    <p className="text-sm">
                                        - {props.review.author?.length > 1 ? props.review.author : 'Anonymous'}
                                    </p>
                                </div>
                                <div className="col-span-5">
                                    <p className="text-right text-sm text-slate-600">
                                        <small>{props.review.dates.published_human}</small>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Verification, review ID and date */}
                    {props?.keepMinimal === false && (
                        <div className="col-span-12 mt-2 grid w-full grid-cols-12 gap-4">
                            <div className="col-span-6">
                                {props?.review?.platform === 'workingfeedback' ? (
                                    <p className="text-sm">
                                        <small>
                                            <CheckCheck className="inline-block h-3 w-3" aria-hidden="true" /> Verified review ({props?.review?.id})
                                        </small>
                                    </p>
                                ) : (
                                    <p className="text-sm">
                                        <small>{props?.review?.id}</small>
                                    </p>
                                )}
                            </div>
                            <div className="col-span-6 text-right">
                                <p className="text-sm leading-tight text-slate-600">
                                    <small>
                                        Review left {props.review.dates.published_human}
                                        <br />
                                        <i>{attributionText(props?.review?.collection_method_text)}.</i>
                                    </small>
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Reply box */}
                    {props?.hideReplies !== true && props?.review?.hasReply && (
                        <div className="col-span-12">
                            <ReplyCard company={props?.review?.clientName} reply={props?.review?.reply} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
